/**
 * Scrolling bar waveform visualization — Canvas-based
 *
 * Matches the style of the main AI Scribe's AudioWaveform.tsx:
 * vertical bars with rounded corners, scrolling right-to-left,
 * driven by real-time audio level data.
 *
 * Adapted for public recording with purple/indigo brand colors,
 * 48px height, dark mode support, and DPR-aware rendering.
 */

import { useCallback, useEffect, useRef } from 'react';

interface WaveformCanvasProps {
  audioLevel: number;
  isActive: boolean;
  isPaused: boolean;
  className?: string;
}

// ─── Bar Configuration ───────────────────────────────────────────────
const BAR_WIDTH = 2;
const BAR_GAP = 1;
const CORNER_RADIUS = 1;
const INSERT_EVERY_N_FRAMES = 3;
const MIN_BAR_HEIGHT = 1.5;
const MAX_BAR_RATIO = 0.80;        // bars use up to 80% of canvas height (keeps ~10% gap top/bottom)
const SMOOTHING_FACTOR = 0.18;     // EMA responsiveness (higher = snappier)

// ─── Colors ──────────────────────────────────────────────────────────
const COLORS = {
  light: {
    barCenter: '#8B5CF6',           // eyevantage primary (purple-500)
    barEdge: '#6366F1',             // indigo-500
    pausedBar: 'rgba(139, 92, 246, 0.3)',
    flatline: 'rgba(139, 92, 246, 0.12)',
  },
  dark: {
    barCenter: '#A78BFA',           // purple-400
    barEdge: '#818CF8',             // indigo-400
    pausedBar: 'rgba(167, 139, 250, 0.3)',
    flatline: 'rgba(167, 139, 250, 0.15)',
  },
};

export const WaveformCanvas = ({
  audioLevel,
  isActive,
  isPaused,
  className = '',
}: WaveformCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number | null>(null);
  const isDarkRef = useRef(false);

  // Audio state refs — avoid React re-renders in animation loop
  const audioLevelRef = useRef(0);
  const isActiveRef = useRef(isActive);
  const isPausedRef = useRef(isPaused);
  const smoothedLevelRef = useRef(0);

  // Waveform data
  const waveformHistoryRef = useRef<number[]>([]);
  const frameCounterRef = useRef(0);

  // ─── Keep refs in sync ─────────────────────────────────────────────
  useEffect(() => { audioLevelRef.current = audioLevel; }, [audioLevel]);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  // ─── Dark mode detection ───────────────────────────────────────────
  useEffect(() => {
    const check = () => {
      isDarkRef.current = document.documentElement.classList.contains('dark');
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  // ─── Canvas resize (DPR-aware) ────────────────────────────────────
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const dpr = window.devicePixelRatio || 1;
    const { width, height } = parent.getBoundingClientRect();
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);
  }, []);

  // ─── Draw a single rounded-corner bar (matches AudioWaveform.tsx) ─
  const drawBar = useCallback((
    ctx: CanvasRenderingContext2D,
    x: number,
    centerY: number,
    barHeight: number,
  ) => {
    const h = Math.max(MIN_BAR_HEIGHT, barHeight);
    const y = centerY - h / 2;
    const r = Math.min(CORNER_RADIUS, h / 2, BAR_WIDTH / 2);

    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + BAR_WIDTH - r, y);
    ctx.quadraticCurveTo(x + BAR_WIDTH, y, x + BAR_WIDTH, y + r);
    ctx.lineTo(x + BAR_WIDTH, y + h - r);
    ctx.quadraticCurveTo(x + BAR_WIDTH, y + h, x + BAR_WIDTH - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.fill();
  }, []);

  // ─── Main animation loop ──────────────────────────────────────────
  useEffect(() => {
    resizeCanvas();
    const onResize = () => resizeCanvas();
    window.addEventListener('resize', onResize);

    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const W = canvas.width / dpr;
      const H = canvas.height / dpr;
      const centerY = H / 2;
      const active = isActiveRef.current;
      const paused = isPausedRef.current;
      const colors = isDarkRef.current ? COLORS.dark : COLORS.light;

      const step = BAR_WIDTH + BAR_GAP;
      const maxBars = Math.floor(W / step);
      const maxBarHeight = H * MAX_BAR_RATIO;

      // ── Smooth the audio level ──────────────────────────────────
      const target = (active && !paused) ? audioLevelRef.current : 0;
      smoothedLevelRef.current += (target - smoothedLevelRef.current) * SMOOTHING_FACTOR;
      const level = smoothedLevelRef.current;

      // ── Push new bar data (throttled, like AudioWaveform) ───────
      if (active && !paused) {
        frameCounterRef.current++;
        if (frameCounterRef.current % INSERT_EVERY_N_FRAMES === 0) {
          const barHeight = Math.max(MIN_BAR_HEIGHT, level * maxBarHeight);
          waveformHistoryRef.current.push(barHeight);
          if (waveformHistoryRef.current.length > maxBars) {
            waveformHistoryRef.current.shift();
          }
        }
      }

      // ── Smooth scroll offset — interpolate between bar insertions ─
      const framesInCycle = frameCounterRef.current % INSERT_EVERY_N_FRAMES;
      const scrollProgress = (active && !paused)
        ? framesInCycle / INSERT_EVERY_N_FRAMES
        : 0;
      const scrollOffset = scrollProgress * step;

      // ── Clear ───────────────────────────────────────────────────
      ctx.clearRect(0, 0, W, H);

      const history = waveformHistoryRef.current;

      // ── Draw flatline when no bars ──────────────────────────────
      if (history.length === 0) {
        ctx.fillStyle = colors.flatline;
        ctx.fillRect(0, centerY - 0.5, W, 1);
        animFrameRef.current = requestAnimationFrame(draw);
        return;
      }

      // ── Create bar gradient (once per frame, reused for all bars)
      if (paused) {
        ctx.fillStyle = colors.pausedBar;
      } else {
        const grad = ctx.createLinearGradient(0, centerY - maxBarHeight / 2, 0, centerY + maxBarHeight / 2);
        grad.addColorStop(0, colors.barEdge);
        grad.addColorStop(0.5, colors.barCenter);
        grad.addColorStop(1, colors.barEdge);
        ctx.fillStyle = grad;
      }

      // ── Draw bars right-to-left with smooth sub-pixel scrolling ─
      const baseOffsetX = W - history.length * step;
      for (let i = 0; i < history.length; i++) {
        const x = baseOffsetX + i * step - scrollOffset;
        if (x + BAR_WIDTH < 0 || x > W) continue; // skip off-screen
        drawBar(ctx, x, centerY, history[i]);
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', onResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [resizeCanvas, drawBar]);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <canvas
        ref={canvasRef}
        className="block w-full h-full"
        style={{ willChange: 'transform' }}
      />
    </div>
  );
};
